// 药库管理
import { Button, message, Modal, Popconfirm, Icon, Progress } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import CommonTable, { ICommonTable } from 'src/components/common/CommonTable';
import MallMedicineExportButton from 'src/components/common/MallMedicineExportButton';
import MallMedicineUploadButton from 'src/components/common/MallMedicineUploadButton';
import { EnumCustomControl } from 'src/constants/enum';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IMallBrand, IMallPharmacyDetail, IMallPharmacySearch } from 'src/entity/MallEntity';
import { IStateTask } from 'src/entity/StateEntity';
import { fetchMallBrandIfNeeded } from 'src/redux/actions/mallBrandCreator';
import I18N from 'src/services/I18NService';
import { MallMedicineType, MallService } from 'src/services/MallService';
import OrderService from 'src/services/OrderService';
import { IMallPharmacyActive } from '../../../entity/MallEntity';
import PharmacySearchForm from './PharmacySearchForm';
import { STATUS_PROGRESS } from 'src/constants/ActionTypes';
import { reloadMallPharmacy } from 'src/redux/actions/mallPharmacyCreator';
import { IResultError } from 'src/entity/BaseResult';
import { MallEdiService } from 'src/services/MallEdiService';

interface IPharmacyListProps extends RouteComponentProps {
  isAgent: boolean;
  userInfo: IAgentInfo | IDrugStoreInfo;
  brandList: IMallBrand[];
  taskState: IStateTask;
  dispatch: Dispatch<any>;
}

interface IPharmacyListState extends ICommonTable<IMallPharmacyDetail> {
  query: IMallPharmacySearch;
  loadingBtn: boolean;
  visibleActive: boolean;
  /** 是否开通药品订单接口 */
  isOpenMedicine: boolean;
}

class MallPharmacyListPage extends React.Component<IPharmacyListProps, IPharmacyListState> {
  constructor(props: IPharmacyListProps) {
    super(props);

    this.state = {
      rowKey: 'uuid',
      loadingTable: false,
      columns: [],
      dataSource: [],
      query: {
        seller_user: props.userInfo.seller_user,
        is_deleted: false,
        offset: 0,
        limit: 10,
      },
      loadingBtn: false,
      visibleActive: false,
      isOpenMedicine: false,
    } as IPharmacyListState;
  }

  async componentDidMount() {
    await this.props.dispatch(fetchMallBrandIfNeeded());
    await this.getSendOrderMedicineConfig()

    const columns: Array<ColumnProps<IMallPharmacyDetail>> = [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
      },
      {
        title: I18N.get('common_name_desc'),
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: I18N.get('pharmacy_brand'),
        key: 'brand',
        dataIndex: 'brand',
        render: (text: any, record: IMallPharmacyDetail, index: number): React.ReactNode => {
          const brandObj = this.props.brandList.find((t) => t.uuid === record.brand);
          return !!brandObj ? brandObj.name : '';
        },
      },
      {
        title: I18N.get('pharmacy_dosage_form'),
        key: 'dosage_form',
        dataIndex: 'dosage_form',
        render: (text: any, record: IMallPharmacyDetail, index: number): React.ReactNode => {
          const obj = MallMedicineType.find((t) => t.value === record.dosage_form);
          return !!obj ? I18N.get(obj.key) : '';
        },
      },
      {
        title: I18N.get('order_category'),
        key: 'order_type',
        dataIndex: 'order_type',
        render: (text: any, record: IMallPharmacyDetail, index: number): React.ReactNode => {
          return OrderService.getOrderType(record.order_type);
        },
      },
      {
        title: I18N.get('common_status'),
        key: 'is_active',
        dataIndex: 'is_active',
        render: (text: any, record: IMallPharmacyDetail, index: number): React.ReactNode => {
          return !!record.is_active ? I18N.get('common_status_start') : <span style={{ color: '#F5222D' }}>{I18N.get('common_status_disable')}</span>;
        },
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        width: 220,
        render: (text: any, record: IMallPharmacyDetail, index: number): React.ReactNode => {
          return (
            <div style={{ display: 'flex' }}>
              <a onClick={() => this.showDetail(record)}>{I18N.get('common_detail')}</a>
              &nbsp;&nbsp;
              {/* 导入 */}
              <MallMedicineUploadButton
                title={I18N.get('common_import')}
                isAgent={this.props.isAgent}
                pharmacy={record}
                control={EnumCustomControl.A}
                userInfo={this.props.userInfo}
                isOpenMedicine={this.state.isOpenMedicine}
                dispatch={this.props.dispatch}
              />
              &nbsp;&nbsp;
              {/* 导出 */}
              <MallMedicineExportButton title={I18N.get('common_export')} pharmacy={record} control={EnumCustomControl.A} />
              &nbsp;&nbsp;
              <Popconfirm
                onConfirm={() => this.activeStatusChange(record)}
                title={`${I18N.get('common_sure')}${!!record.is_active ? I18N.get('common_status_disable') : I18N.get('common_status_start')}`}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <a>{!!record.is_active ? I18N.get('common_status_disable') : I18N.get('common_status_start')}</a>
              </Popconfirm>
              &nbsp;&nbsp;
              <Popconfirm
                onConfirm={() => this.deletePharmacy(record)}
                title={`${I18N.get('pharmacy_delete_confirm')}`}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <a>{I18N.get('common_delete')}</a>
              </Popconfirm>
            </div>
          );
        },
      },
    ];

    this.setState({
      columns,
    });
    await this.handleSearch();
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /**
   * 查询
   *
   * @memberof PharmacyList
   */
  handleSearch = async (offset: number = 0, limit: number = 10) => {
    const query: IMallPharmacySearch = {
      seller_user: this.props.userInfo.seller_user,
      name: this.state.query.name,
      is_deleted: false,
      limit,
      offset,
    };

    await this.doSearch(query);
  };

  /**
   * 查询
   *
   * @memberof PharmacyList
   */
  doSearch = async (query: IMallPharmacySearch) => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      query.seller_user = this.props.userInfo.seller_user;

      this.setState({
        loadingTable: true,
        query,
      });

      const res = await MallService.searchPharmacy(query);
      if (!!res) {
        this.setState({
          dataSource: res.results,
          total: res.count,
        });
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingTable: false,
      });
    }
  };

  /**
   * 添加药库
   *
   * @memberof PharmacyList
   */
  addPharmacy = () => {
    const { match } = this.props;
    this.props.history.push(`${match.path}/add`);
  };

  /**
   * 详情
   *
   * @memberof PharmacyList
   */
  showDetail = (record: IMallPharmacyDetail) => {
    if (!record) {
      return;
    }
    const { match } = this.props;
    this.props.history.push(`${match.path}/${record.uuid}`);
  };

  /**
   * 启用/禁用
   *
   * @memberof PharmacyList
   */
  activeStatusChange = async (record: IMallPharmacyDetail) => {
    if (this.state.loadingBtn) {
      return;
    }
    try {
      if (!!record && !!record.uuid) {
        this.setState({
          loadingBtn: true,
        });

        const isActive = !record.is_active;
        const res = await MallService.changePharmacyStatus(record.uuid, isActive);
        if (!!res) {
          if (res.success) {
            this.props.dispatch(reloadMallPharmacy());
            if (res.data.is_active === isActive) {
              // 启用禁用成功
              await this.handleSearch();
            }
            // else if (isActive) {
            //   // 启用/
            //   // this.activeInfo(res.data);
            // }
          } else {
            this.errorHanding(res);
          }
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingBtn: false,
      });
    }
  };

  /**
   * 删除药库
   *
   */
  deletePharmacy = async (record: IMallPharmacyDetail) => {
    if (this.state.loadingBtn) {
      return;
    }
    try {
      if (!!record && !!record.uuid) {
        this.setState({
          loadingBtn: true,
        });

        const res = await MallService.deletePharmacy(record.uuid);
        if (!!res) {
          message.success(I18N.get('common_submit_success'));
          await this.handleSearch();
          this.props.dispatch(reloadMallPharmacy());
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingBtn: false,
      });
    }
  };

  /**
   * 启用错误处理
   */
  errorHanding(info: IResultError<IMallPharmacyActive>) {
    Modal.warning({
      width: 800,
      title: `${I18N.get('common_status_start')}${I18N.get('common_tips')}`,
      okText: `${I18N.get('common_ok')}`,
      content: (
        <div>
          {/* {info.data.medicine_count < 300 && (
            <p>
              1、{I18N.get('pharmacy_active_msg_count')}, {I18N.get('pharmacy_active_msg_current_count')}: {info.data.medicine_count}
            </p>
          )} */}
          {!info.data.all_price_not_null && (
            <p>
              {/* {info.data.medicine_count < 300 ? '2、' : '1、'} */}
              1、{I18N.get('pharmacy_active_msg_price_is_null')}
            </p>
          )}
        </div>
      ),
      onOk() { },
    });
  }

  /**
   * 配药中心是否开启药品对接
   */
  private getSendOrderMedicineConfig = async () => {
    if (!this.props.isAgent) {
      try {
        const config = await MallEdiService.getSendOrderMedicineConfig();
        if (!!config) {
          this.setState({
            isOpenMedicine: config.is_enabled,
          })
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  render() {
    return (
      <div className="pharmacy-page">
        <PharmacySearchForm cbSearch={this.doSearch} />

        <div className="pharmacy-content">
          <span className="pharmacy-content-desc">
            {I18N.get('common_total_prefix')}
            {this.state.total || 0}
            {I18N.get('pharmacy_total_suffix')}
          </span>
          <Button type="primary" onClick={this.addPharmacy}>
            <Icon type="plus" />
            {I18N.get('pharmacy_add')}
          </Button>
        </div>

        <CommonTable
          rowKey={this.state.rowKey}
          showPagination={(this.state.total || 0) > 0}
          columns={this.state.columns}
          loadingTable={this.state.loadingTable}
          dataSource={this.state.dataSource}
          total={this.state.total}
          offset={this.state.query.offset}
          limit={this.state.query.limit}
          callback={this.handleSearch}
        />

        <Modal title="" footer={null} closable={false} centered={true} visible={this.props.taskState.type === STATUS_PROGRESS}>
          <div style={{ textAlign: 'center' }}>
            <Progress type="circle" percent={this.props.taskState.percentage} />
          </div>
        </Modal>
      </div>
    );
  }
}

export default MallPharmacyListPage;



// WEBPACK FOOTER //
// ./src/components/views/Mall/PharmacyList.tsx